import React, { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { useFacility } from "@/context/FacilityContext";
import { PageHeader } from "@/components/common/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, Loader2, Trash2, IndianRupee } from "lucide-react";
import { toast } from "sonner";

const CATS = ["Procedure", "Consumables", "Nursing", "Investigation", "Consultation", "Room / Bed", "Surgery", "General"];

export default function ChargeManagement() {
  const { activeId, has, activeFacility } = useFacility();
  const canManage = has("bed:create");
  const [rows, setRows] = useState(null);
  const [form, setForm] = useState({ name: "", category: "Procedure", amount: "" });
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    if (!activeId) return;
    setRows(null);
    api.get(`/ipd/charge-catalog?facility_id=${activeId}`).then((r) => setRows(r.data)).catch(() => setRows([]));
  }, [activeId]);
  useEffect(load, [load]);

  const add = async () => {
    if (!form.name.trim() || form.amount === "") { toast.error("Name and amount are required"); return; }
    setSaving(true);
    try {
      await api.post("/ipd/charge-catalog", { ...form, amount: parseFloat(form.amount), facility_id: activeId });
      toast.success(`Added "${form.name}"`);
      setForm({ name: "", category: form.category, amount: "" });
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setSaving(false); }
  };
  const remove = async (r) => {
    try { await api.delete(`/ipd/charge-catalog/${r.id}`); toast.success("Charge removed"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div data-testid="charge-management-page">
      <PageHeader title="Charge Management"
        subtitle={activeFacility ? `Fixed charge list for ${activeFacility.name}. These rates are used across inpatient billing.` : "Select a facility to manage charges."} />

      {canManage && (
        <div className="bg-white border border-slate-200 rounded-md p-5 mb-6" data-testid="add-charge-card">
          <div className="flex items-center gap-2 text-slate-800 font-semibold mb-4"><Plus className="h-5 w-5 text-blue-600" /> Add Charge</div>
          <div className="grid grid-cols-1 md:grid-cols-[2fr_1.2fr_1fr_auto] gap-3 items-end">
            <div className="space-y-1.5"><Label className="text-xs">Charge name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. IV Cannulation" data-testid="charge-name-input" /></div>
            <div className="space-y-1.5"><Label className="text-xs">Category</Label>
              <select className="w-full h-9 rounded-md border border-slate-300 px-3 text-sm bg-white" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="charge-category-select">
                {CATS.map((c) => <option key={c}>{c}</option>)}
              </select></div>
            <div className="space-y-1.5"><Label className="text-xs">Amount (₹)</Label><Input type="number" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} placeholder="0" data-testid="charge-amount-input" /></div>
            <Button onClick={add} disabled={saving} className="bg-blue-700 hover:bg-blue-800 h-9" data-testid="charge-save-button">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Add"}</Button>
          </div>
          <p className="text-[11px] text-slate-400 mt-2">Charges are fixed once added. Staff pick from this list when billing inpatients.</p>
        </div>
      )}

      <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
        {rows === null ? <div className="grid place-items-center py-16"><Loader2 className="h-5 w-5 animate-spin text-blue-600" /></div> : (
          <Table>
            <TableHeader><TableRow className="bg-slate-50"><TableHead>Charge</TableHead><TableHead>Category</TableHead><TableHead className="text-right">Amount (₹)</TableHead><TableHead className="text-right">Actions</TableHead></TableRow></TableHeader>
            <TableBody>
              {rows.map((r) => (
                <TableRow key={r.id} className="hover:bg-slate-50" data-testid={`charge-row-${r.id}`}>
                  <TableCell className="font-medium text-slate-800">{r.name}</TableCell>
                  <TableCell><span className="text-xs bg-slate-100 text-slate-600 rounded px-2 py-0.5">{r.category}</span></TableCell>
                  <TableCell className="text-right font-semibold text-slate-800 flex items-center justify-end"><IndianRupee className="h-3.5 w-3.5" />{Number(r.amount).toLocaleString()}</TableCell>
                  <TableCell className="text-right">{has("bed:delete") && <button onClick={() => remove(r)} className="text-slate-300 hover:text-red-500" data-testid={`charge-delete-${r.id}`}><Trash2 className="h-4 w-4" /></button>}</TableCell>
                </TableRow>
              ))}
              {rows.length === 0 && <TableRow><TableCell colSpan={4} className="text-center text-slate-400 py-12">No charges yet. Add your hospital's standard charge list above.</TableCell></TableRow>}
            </TableBody>
          </Table>
        )}
      </div>
    </div>
  );
}
